import React, { useEffect } from "react";
import { useFaultEvents } from "../../../../../api/useFaultEvents";
import { type Project } from "../../../../../api/useProjects";
import LoadingBoundary from "@ui/LoadingBoundary";
import SidePanelLayout from "@ui/SidePanel/Layout";
import { css } from "@linaria/core";
import { spacings } from "@ui/Theme";
import NavV2 from "@ui/NavV2";
import Tab from "@ui/NavV2/Tab";
import t from "../../../../../i18n";
import {
  Navigate,
  Outlet,
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
} from "react-router";
import Loading from "@ui/Loading";
import { useProjectSource } from "../../../../../api/useSources";
import { toPath, useMatchPattern } from "../../../../../router/helpers/path";
import { useFaultEventAccess } from "../../hooks/useFaultEventAccess";
import Drawer from "@ui/Drawer";
import { useEvents } from "../../../../../api/useEvents";
import { useSettings } from "../../../../../api/useSettings";
import DrawerCreateManualFaultEvent from "../../components/Drawers/DrawerFormCreateManualFaultEvent";
import { createManualFaultEventDrawerParamURL } from "../../utils/event";
import Secondary from "@ui/Header/Secondary";
import { ProjectGlobalSidePanelLayout } from "../../../GlobalSidePanelLayout";

export function Navigation() {
  const { projectId } = useParams();
  if (!projectId) throw new Error("no project");

  const matchOpenedEvent = useMatchPattern("/projects/:projectId/fault-events");
  const matchClosedEvent = useMatchPattern("/projects/:projectId/fault-events/closed");

  return (
    <NavV2>
      <Tab
        key="liveFaultEvents"
        data-section-id="liveFaultEvents"
        to={toPath("/projects/:projectId/fault-events", { projectId })}
        active={matchOpenedEvent !== null}
      >
        {t("project.fault_events.tab.in_progress")}
      </Tab>
      <Tab
        key="closedFaultEvents"
        data-section-id="closedFaultEvents"
        to={toPath("/projects/:projectId/fault-events/closed", { projectId })}
        active={matchClosedEvent !== null}
      >
        {t("project.fault_events.tab.closed")}
      </Tab>
    </NavV2>
  );
}

export interface FaultEventsOutletContext {
  project: Project;
  source: ReturnType<typeof useProjectSource>;
  faultEvents: ReturnType<typeof useFaultEvents>;
  openCreateDrawer: () => void;
}

export interface EventsOutletContext {
  project: Project;
  source: ReturnType<typeof useProjectSource>;
  events: ReturnType<typeof useEvents>;
  openCreateDrawer: () => void;
}

const Events = () => {
  const [searchParams] = useSearchParams();
  const settings = useSettings();
  const navigate = useNavigate();
  const hasEventAccess = useFaultEventAccess();

  const project = useOutletContext<Project>();
  if (!project) throw new Error("no project");

  const createManualFaultRef = React.useRef<{
    resetForm: () => void;
  }>(null);
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = React.useState(false);
  const handleCloseDrawer = React.useCallback(() => {
    createManualFaultRef.current?.resetForm();
    setIsCreateDrawerOpen(false);
    if (searchParams.get(createManualFaultEventDrawerParamURL) === "true") {
      searchParams.delete(createManualFaultEventDrawerParamURL);
      void navigate({ search: searchParams.toString() });
    }
  }, [createManualFaultRef, searchParams, navigate]);

  const source = useProjectSource(project);
  const events = useEvents(project.id, settings.MANUAL_EVENTS_DEV_MODE_ENABLED);

  const authorizedEvents = React.useMemo(() => {
    if (!settings.MANUAL_EVENTS_DEV_MODE_ENABLED)
      return events.filter(event => event.type === "DIAGNOSED");
    return events;
  }, [events, settings.MANUAL_EVENTS_DEV_MODE_ENABLED]);

  useEffect(() => {
    if (searchParams.get(createManualFaultEventDrawerParamURL) === "true") {
      setIsCreateDrawerOpen(true);
    }
  }, [searchParams]);

  if (source === null) return <Loading />;

  if (!hasEventAccess)
    return <Navigate to={toPath("/projects/:projectId", { projectId: project.id })} replace />;

  return (
    <ProjectGlobalSidePanelLayout project={project}>
      <SidePanelLayout className={Panel}>
        <Secondary className={secondary} nav={<Navigation />} />
        <LoadingBoundary>
          <div className={content}>
            <Outlet
              context={{
                project,
                source,
                events: authorizedEvents,
                openCreateDrawer: () => setIsCreateDrawerOpen(true),
              }}
            />
            {settings.MANUAL_EVENTS_DEV_MODE_ENABLED && (
              <Drawer
                title={t("projet.events.create_manual_fault_event.drawer.title")}
                isOpen={isCreateDrawerOpen}
                onRequestClose={handleCloseDrawer}
                className={noPaddingDrawer}
              >
                <DrawerCreateManualFaultEvent project={project} onCloseDrawer={handleCloseDrawer} />
              </Drawer>
            )}
          </div>
        </LoadingBoundary>
      </SidePanelLayout>
    </ProjectGlobalSidePanelLayout>
  );
};

export default Events;

//#region CSS
const Panel = css`
  --thead-sticky-offset: 0px;
  gap: 0;
  padding: 0 !important;
`;
const content = css`
  gap: 0;
  padding: 0 ${spacings.xxl} ${spacings.l} ${spacings.xxl} !important;
`;
const noPaddingDrawer = css`
  & > * {
    padding: 0;

    & > :nth-child(1) {
      padding: ${spacings.m} ${spacings.m};
    }

    & > :nth-child(2) {
      overflow-y: auto;
      overscroll-behavior: contain;
    }
  }
`;
const secondary = css`
  margin-bottom: ${spacings.l};
  padding: 0 ${spacings.xxl};
`;
//#endregion CSS
